import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { BottomSheet } from '@/components/base/BottomSheet';
import { EmptyState } from '@/components/base/EmptyState';
import { showToast } from '@/components/base/Toast';
import { DebtReminderModal } from '@/components/feature/DebtReminderModal';
import { useStore } from '@/hooks/useStore';
import { useAuth } from '@/hooks/useAuth';
import {
  getFarmersByHtx,
  getDebtsByFarmer,
  getActivePaymentIntentByLoan,
  htxConfirmReceipt,
} from '@/store';
import type { Debt, PaymentIntent } from '@/types';
import { formatCurrencyCompact } from '@/utils/formatters';

/** Loan approval/grant-limit/disbursement/Antesco-funding quick actions were removed
 * from this sheet — they're Agrida-internal actions with no accessible endpoint for
 * HTX_STAFF (see task 27 notes). "debts" is the one real, HTX-accessible action left:
 * confirming a farmer's repayment via the real PaymentIntent flow. */
export type HomeFinanceAction = 'debts';

export function HomeFinanceQuickSheets({ action, onClose }: { action: HomeFinanceAction | null; onClose: () => void }) {
  useStore();
  const { user } = useAuth();
  const [selectedDebt, setSelectedDebt] = useState<Debt | null>(null);
  const [reminderDebt, setReminderDebt] = useState<Debt | null>(null);

  useEffect(() => {
    setSelectedDebt(null);
    setReminderDebt(null);
  }, [action]);

  const closeAll = () => {
    setSelectedDebt(null);
    setReminderDebt(null);
    onClose();
  };

  const farmers = getFarmersByHtx(user?.htxId);
  const farmerName = (farmerId: string) => farmers.find((item) => item.id === farmerId)?.name || farmerId;
  const piAttentionStatuses = ['farmer_confirmed_transfer', 'waiting_htx_confirmation', 'reconcile_failed'];
  const actionableDebts = farmers
    .flatMap((f) => getDebtsByFarmer(f.id))
    .map((debt) => ({ debt, pi: getActivePaymentIntentByLoan(debt.farmerId, debt.advanceRequestId || debt.id) }))
    .filter(({ debt, pi }) => debt.conNo > 0 && (debt.status === 'Quá hạn' || (!!pi && piAttentionStatuses.includes(pi.status))))
    .sort((a, b) => (b.pi?.soTien || b.debt.conNo) - (a.pi?.soTien || a.debt.conNo));

  if (reminderDebt) {
    return <DebtReminderModal open debt={reminderDebt} farmerName={farmerName(reminderDebt.farmerId)} htxName={user?.displayName} onClose={() => setReminderDebt(null)} />;
  }

  if (selectedDebt) {
    return (
      <DebtActionSheet
        debt={selectedDebt}
        paymentIntent={getActivePaymentIntentByLoan(selectedDebt.farmerId, selectedDebt.advanceRequestId || selectedDebt.id)}
        farmerName={farmerName(selectedDebt.farmerId)}
        onReminder={() => setReminderDebt(selectedDebt)}
        onClose={closeAll}
      />
    );
  }

  if (action === 'debts') {
    return (
      <SelectionSheet open title="Công nợ cần xử lý" onClose={closeAll} empty={actionableDebts.length === 0} emptyTitle="Không có công nợ cần xử lý">
        {actionableDebts.map(({ debt, pi }) => (
          <QuickItem
            key={debt.id}
            title={farmerName(debt.farmerId)}
            caption={`${debt.maKhoanVay || debt.id} · ${pi ? (pi.status === 'reconcile_failed' ? 'Đối soát lỗi' : 'Chờ HTX xác nhận') : debt.status}`}
            value={formatCurrencyCompact(pi?.soTien || debt.conNo)}
            tone="rose"
            onClick={() => setSelectedDebt(debt)}
          />
        ))}
      </SelectionSheet>
    );
  }

  return null;
}

function SelectionSheet({ open, title, onClose, empty, emptyTitle, children }: { open: boolean; title: string; onClose: () => void; empty: boolean; emptyTitle: string; children: ReactNode }) {
  return (
    <BottomSheet open={open} onClose={onClose} title={title}>
      {empty ? <EmptyState icon="ri-inbox-2-line" title={emptyTitle} description="Dữ liệu sẽ tự cập nhật khi có phát sinh mới." /> : <div className="space-y-2">{children}</div>}
    </BottomSheet>
  );
}

function QuickItem({ title, caption, value, tone = 'emerald', onClick }: { title: string; caption: string; value: string; tone?: 'emerald' | 'rose'; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="mobile-card flex w-full min-w-0 items-center justify-between gap-3 p-3.5 text-left active:scale-[0.99]">
      <span className="min-w-0">
        <strong className="block truncate text-sm text-slate-900">{title}</strong>
        <span className="mt-1 block line-clamp-2 text-xs text-slate-500">{caption}</span>
      </span>
      <span className={`shrink-0 text-right text-sm font-bold ${tone === 'rose' ? 'text-rose-600' : 'text-agrida-700'}`}>{value}</span>
    </button>
  );
}

function DebtActionSheet({
  debt,
  paymentIntent,
  farmerName,
  onReminder,
  onClose,
}: {
  debt: Debt;
  paymentIntent?: PaymentIntent;
  farmerName: string;
  onReminder: () => void;
  onClose: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const canConfirm = !!paymentIntent && ['farmer_confirmed_transfer', 'waiting_htx_confirmation', 'reconcile_failed'].includes(paymentIntent.status);

  const handleConfirm = async () => {
    if (!paymentIntent) return;
    setConfirming(true);
    try {
      await htxConfirmReceipt(paymentIntent.id, debt.farmerId);
      showToast(`Đã xác nhận tất toán cho ${farmerName}`, 'success');
      onClose();
    } catch {
      showToast('Không thể xác nhận, vui lòng thử lại', 'error');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <BottomSheet open title="Công nợ cần xử lý" onClose={onClose}>
      <div className="space-y-3">
        <div className="rounded-2xl bg-slate-50 p-4 text-center">
          <p className="text-sm font-bold text-slate-900">{farmerName}</p>
          <p className="mt-1 text-xs text-slate-500">{debt.maKhoanVay || debt.id} · {debt.status}</p>
          <p className="mt-3 text-xl font-extrabold text-rose-600">{formatCurrencyCompact(paymentIntent?.soTien || debt.conNo)}</p>
        </div>
        {paymentIntent && (
          <div className="rounded-xl border border-sky-100 bg-sky-50 p-3">
            <p className="text-xs font-bold text-sky-800">Giao dịch thanh toán</p>
            <p className="mt-1 text-[11px] text-sky-700">Nội dung CK: {paymentIntent.noiDungChuyenKhoan}</p>
            <p className="mt-1 text-[11px] text-sky-600">Trạng thái: {paymentIntent.status}</p>
          </div>
        )}
        <div className="grid grid-cols-1 gap-2">
          <button type="button" onClick={onReminder} className="min-h-11 rounded-xl border border-amber-200 bg-amber-50 text-xs font-bold text-amber-700">
            <i className="ri-notification-3-line mr-1" />
            Gửi thông báo nhắc nợ
          </button>
          {canConfirm && (
            <button type="button" onClick={handleConfirm} disabled={confirming} className="min-h-11 rounded-xl bg-agrida-600 text-xs font-bold text-white disabled:opacity-60">
              <i className="ri-check-line mr-1" />
              {confirming ? 'Đang xác nhận...' : 'Xác nhận đã nhận tiền'}
            </button>
          )}
        </div>
      </div>
    </BottomSheet>
  );
}
